import React, { useCallback, useMemo } from "react";

/**
 * Lightweight markdown renderer for AI output.
 * Supports: headings, bold, italic, inline code, lists, blockquotes,
 * and clickable [source:N] citation markers when citations are provided.
 */

function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function buildCitationMap(citations) {
  const map = {};
  (citations || []).forEach((c) => {
    if (c?.source_id != null) map[c.source_id] = c;
  });
  return map;
}

function inline(text, citationMap, citationLabel) {
  let s = escapeHtml(text);

  s = s.replace(/\[source:(\d+)\]/gi, (match, id) => {
    const sid = parseInt(id, 10);
    if (citationMap[sid]) {
      return (
        `<button type="button" class="chat-citation-link" ` +
        `data-source-id="${sid}" data-testid="inline-citation-${sid}">` +
        `${escapeHtml(citationLabel)} ${sid}</button>`
      );
    }
    return match;
  });

  s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  return s;
}

function blockToHtml(block, citationMap, citationLabel) {
  const trimmed = block.trim();
  if (!trimmed) return "";

  const h = trimmed.match(/^(#{1,3})\s+(.*)$/m);
  if (h && trimmed.split("\n").length === 1) {
    const level = h[1].length;
    return `<h${level}>${inline(h[2], citationMap, citationLabel)}</h${level}>`;
  }

  if (trimmed.split("\n").every((l) => l.startsWith(">"))) {
    const inner = trimmed
      .split("\n")
      .map((l) => inline(l.replace(/^>\s?/, ""), citationMap, citationLabel))
      .join("<br/>");
    return `<blockquote>${inner}</blockquote>`;
  }

  const lines = trimmed.split("\n");
  const isBullet = lines.every((l) => /^[-*•]\s+/.test(l));
  const isNumbered = lines.every((l) => /^\d+[.)]\s+/.test(l));
  if (isBullet) {
    const items = lines
      .map((l) => `<li>${inline(l.replace(/^[-*•]\s+/, ""), citationMap, citationLabel)}</li>`)
      .join("");
    return `<ul>${items}</ul>`;
  }
  if (isNumbered) {
    const items = lines
      .map((l) => `<li>${inline(l.replace(/^\d+[.)]\s+/, ""), citationMap, citationLabel)}</li>`)
      .join("");
    return `<ol>${items}</ol>`;
  }

  const html = lines
    .map((l) => {
      const hm = l.match(/^(#{1,3})\s+(.*)$/);
      if (hm) return `<h${hm[1].length}>${inline(hm[2], citationMap, citationLabel)}</h${hm[1].length}>`;
      const lm = l.match(/^[-*•]\s+(.*)$/);
      if (lm) return `<li>${inline(lm[1], citationMap, citationLabel)}</li>`;
      return inline(l, citationMap, citationLabel);
    })
    .join("<br/>");
  return `<p>${html}</p>`;
}

export default function Markdown({
  text,
  streaming = false,
  className = "",
  citations = [],
  citationLabel = "Source",
  onCitationClick,
}) {
  const citationMap = useMemo(() => buildCitationMap(citations), [citations]);

  const html = useMemo(() => {
    const blocks = (text || "").split(/\n{2,}/);
    return blocks.map((block) => blockToHtml(block, citationMap, citationLabel)).join("");
  }, [text, citationMap, citationLabel]);

  const handleClick = useCallback(
    (e) => {
      const el = e.target.closest(".chat-citation-link");
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      const sid = parseInt(el.dataset.sourceId, 10);
      const citation = citationMap[sid];
      if (citation) onCitationClick?.(citation);
    },
    [citationMap, onCitationClick],
  );

  return (
    <div
      className={["prose-legal", streaming ? "stream-caret" : "", className].join(" ")}
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
